import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  AppBar,
  Toolbar,
  Typography,
  IconButton,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Menu,
  MenuItem,
  Pagination,
  InputAdornment,
  Card,
  CardContent
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import SearchIcon from '@mui/icons-material/Search';
import AddIcon from '@mui/icons-material/Add';
import VisibilityIcon from '@mui/icons-material/Visibility';
import DeleteIcon from '@mui/icons-material/Delete';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import PersonIcon from '@mui/icons-material/Person';
// import AccessTimeIcon from '@mui/icons-material/AccessTime';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import CodeIcon from '@mui/icons-material/Code';
import axios from '../api/axios';

export default function Sessions() {
  const navigate = useNavigate();
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedSession, setSelectedSession] = useState(null);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [menuSession, setMenuSession] = useState(null);
  const [codeEditorData, setCodeEditorData] = useState(null);
  const [loadingCodeData, setLoadingCodeData] = useState(false);

  useEffect(() => {
    fetchSessions();
  }, [page]);

  const fetchSessions = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`/interview-sessions?page=${page}`);
      setSessions(response.data.data || response.data);
      setTotalPages(response.data.last_page || 1);
      setError('');
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to load sessions');
    } finally {
      setLoading(false);
    }
  };

  const handleMenuOpen = (event, session) => {
    setAnchorEl(event.currentTarget);
    setMenuSession(session);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setMenuSession(null);
  };

  const handleViewSession = async (session) => {
    setSelectedSession(session);
    setViewDialogOpen(true);
    handleMenuClose();
    
    // Fetch code editor data if session is active or completed
    if (session.status === 'active' || session.status === 'completed') {
      setLoadingCodeData(true);
      try {
        const response = await axios.get(`/code-editor-data/session/${session.id}`);
        setCodeEditorData(response.data.data);
      } catch (err) {
        console.log('No code editor data found for this session');
        setCodeEditorData(null);
      } finally {
        setLoadingCodeData(false);
      }
    }
  };

  const handleDeleteSession = async (session) => {
    if (!window.confirm(`Are you sure you want to delete the session with ${session.candidate_name}?`)) {
      return;
    }

    try {
      await axios.delete(`/interview-sessions/${session.id}`);
      fetchSessions();
      handleMenuClose();
    } catch (err) {
      alert(err.response?.data?.message || 'Failed to delete session');
    }
  };

  const handleStartSession = async (session) => {
    try {
      await axios.post(`/interview-sessions/${session.id}/start`);
      fetchSessions();
      handleMenuClose();
    } catch (err) {
      alert(err.response?.data?.message || 'Failed to start session');
    }
  };

  const handleViewCode = (session) => {
    // Navigate to editor with the session's room code
    navigate(`/editor?room=${session.room_code}`);
    handleMenuClose();
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'active':
        return 'success';
      case 'completed':
        return 'info';
      case 'scheduled':
        return 'warning';
      case 'cancelled':
        return 'error';
      default:
        return 'default';
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const filteredSessions = sessions.filter(session =>
    session.candidate_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    session.candidate_email?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const getSessionDuration = (startedAt, endedAt) => {
    if (!startedAt || !endedAt) return 'N/A';
    const start = new Date(startedAt);
    const end = new Date(endedAt);
    const diffMs = end - start;
    const diffMins = Math.floor(diffMs / 60000);
    const hours = Math.floor(diffMins / 60);
    const minutes = diffMins % 60;
    return hours > 0 ? `${hours}h ${minutes}m` : `${minutes}m`;
  };

  const handleCreateSession = async () => {
    try {
      const roomCode = `private-room-${Math.random().toString(36).substr(2, 9)}`;
      const response = await axios.post('/interview-sessions/quick-start', {
        room_code: roomCode,
      });
      if (response.status === 201) {
        navigate(`/editor?room=${roomCode}`);
      } else {
        setError(response.data?.message || 'Failed to create interview session');
        setTimeout(() => navigate('/sessions'), 2000);
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to create interview session');      
      setTimeout(() => navigate('/sessions'), 2000);
    }
  };

  if (loading && sessions.length === 0) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
        <CircularProgress />
        <Typography sx={{ ml: 2 }}>Loading sessions...</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#f5f7fa' }}>
      <AppBar position="static" elevation={0}>
        <Toolbar>
          <IconButton
            color="inherit"
            edge="start"
            onClick={() => navigate('/dashboard')}
            sx={{ mr: 2 }}
          >
            <ArrowBackIcon />
          </IconButton>
          <Typography variant="h6" sx={{ flexGrow: 1, fontWeight: 600 }}>
            Interview Sessions
          </Typography>
          <Button
            color="inherit"
            variant="outlined"
            startIcon={<AddIcon />}
            onClick={() => handleCreateSession()}
            sx={{ borderColor: 'rgba(255,255,255,0.5)' }}
          >
            New Session
          </Button>
        </Toolbar>
      </AppBar>

      <Container maxWidth="xl" sx={{ py: 4 }}>
        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        {/* Search and Stats */}
        <Box sx={{ mb: 3 }}>
          <TextField
            fullWidth
            placeholder="Search by candidate name or email..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
            sx={{ bgcolor: 'white', borderRadius: 1 }}
          />
        </Box>

        {/* Sessions Table */}
        {filteredSessions.length === 0 ? (
          <Paper sx={{ p: 4, textAlign: 'center' }}>
            <CalendarTodayIcon sx={{ fontSize: 60, color: 'text.secondary', mb: 2 }} />
            <Typography variant="h6" gutterBottom>
              No sessions found
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              {searchQuery
                ? 'Try adjusting your search query'
                : 'Start your first interview session'}
            </Typography>
            {!searchQuery && (
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={handleCreateSession}
              >
                Start New Session
              </Button>
            )}
          </Paper>
        ) : (
          <>
            <TableContainer component={Paper} elevation={0}>
              <Table>
                <TableHead>
                  <TableRow sx={{ bgcolor: '#f8f9fa' }}>
                    <TableCell sx={{ fontWeight: 600 }}>Candidate</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Room Code</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Started At</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Duration</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Interviewer</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 600 }}>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredSessions.map((session) => (
                    <TableRow
                      key={session.id}
                      sx={{
                        '&:hover': { bgcolor: '#f8f9fa' },
                        cursor: 'pointer'
                      }}
                      onClick={() => handleViewSession(session)}
                    >
                      <TableCell>
                        <Box>
                          <Typography variant="body2" sx={{ fontWeight: 500 }}>
                            {session.candidate_name || 'N/A'}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {session.candidate_email || 'No email'}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={session.room_code}
                          size="small"
                          sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}
                        />
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={session.status.toUpperCase()}
                          color={getStatusColor(session.status)}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>{formatDate(session.started_at)}</TableCell>
                      <TableCell>{getSessionDuration(session.started_at, session.ended_at)}</TableCell>
                      <TableCell>{session.interviewer?.name || 'N/A'}</TableCell>
                      <TableCell align="right">
                        <IconButton
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleMenuOpen(e, session);
                          }}
                        >
                          <MoreVertIcon />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>

            {/* Pagination */}
            {totalPages > 1 && (
              <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
                <Pagination
                  count={totalPages}
                  page={page}
                  onChange={(e, value) => setPage(value)}
                  color="primary"
                />
              </Box>
            )}
          </>
        )}
      </Container>

      {/* Actions Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
      >
        <MenuItem onClick={() => handleViewSession(menuSession)}>
          <VisibilityIcon fontSize="small" sx={{ mr: 1 }} />
          View Details
        </MenuItem>
        {menuSession?.status === 'scheduled' && (
          <MenuItem onClick={() => handleStartSession(menuSession)}>
            <PlayArrowIcon fontSize="small" sx={{ mr: 1 }} />
            Start Session
          </MenuItem>
        )}
        {menuSession?.status === 'active' && (
          <MenuItem onClick={() => handleViewCode(menuSession)}>
            <CodeIcon fontSize="small" sx={{ mr: 1 }} />
            Join Session
          </MenuItem>
        )}
        <MenuItem onClick={() => handleDeleteSession(menuSession)}>
          <DeleteIcon fontSize="small" sx={{ mr: 1 }} />
          Delete
        </MenuItem>
      </Menu>

      {/* View Session Dialog */}
      <Dialog
        open={viewDialogOpen}
        onClose={() => setViewDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Typography  sx={{ fontWeight: 600 }}>
            Session Details
          </Typography>
        </DialogTitle>
        <DialogContent dividers>
          {selectedSession && (
            <Box sx={{ py: 2 }}>
              <Card sx={{ mb: 2 }}>
                <CardContent>
                  <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', fontWeight: 600 }}>
                    Candidate Information
                  </Typography>
                  <Box sx={{ mt: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                      <PersonIcon sx={{ mr: 1, color: 'text.secondary' }} />
                      <Box>
                        <Typography variant="body2" color="text.secondary">Name</Typography>
                        <Typography variant="body1">{selectedSession.candidate_name || 'N/A'}</Typography>
                      </Box>
                    </Box>
                    {selectedSession.candidate_email && (
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                        <Typography variant="body2" color="text.secondary" sx={{ minWidth: 100 }}>Email</Typography>
                        <Typography variant="body1">{selectedSession.candidate_email}</Typography>
                      </Box>
                    )}
                  </Box>
                </CardContent>
              </Card>

              <Card sx={{ mb: 2 }}>
                <CardContent>
                  <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', fontWeight: 600 }}>
                    Session Information
                  </Typography>
                  <Box sx={{ mt: 2 }}>
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="body2" color="text.secondary">Room Code</Typography>
                      <Chip label={selectedSession.room_code} sx={{ fontFamily: 'monospace', mt: 0.5 }} />
                    </Box>
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="body2" color="text.secondary">Session Link</Typography>
                      <Typography variant="body1">{`${window.location.origin}/codeshare/${selectedSession.room_code}`}</Typography>
                    </Box>
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="body2" color="text.secondary">Status</Typography>
                      <Chip
                        label={selectedSession.status.toUpperCase()}
                        color={getStatusColor(selectedSession.status)}
                        sx={{ mt: 0.5 }}
                      />
                    </Box>
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="body2" color="text.secondary">Started At</Typography>
                      <Typography variant="body1">{formatDate(selectedSession.started_at)}</Typography>
                    </Box>
                    {selectedSession.ended_at && (
                      <Box sx={{ mb: 2 }}>
                        <Typography variant="body2" color="text.secondary">Ended At</Typography>
                        <Typography variant="body1">{formatDate(selectedSession.ended_at)}</Typography>
                      </Box>
                    )}
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="body2" color="text.secondary">Duration</Typography>
                      <Typography variant="body1">
                        {getSessionDuration(selectedSession.started_at, selectedSession.ended_at)}
                      </Typography>
                    </Box>
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="body2" color="text.secondary">Interviewer</Typography>
                      <Typography variant="body1">{selectedSession.interviewer?.name || 'N/A'}</Typography>
                    </Box>
                  </Box>
                </CardContent>
              </Card>

              {selectedSession.notes && (
                <Card sx={{ mb: 2 }}>
                  <CardContent>
                    <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', fontWeight: 600 }}>
                      Notes
                    </Typography>
                    <Typography variant="body1" sx={{ mt: 1, whiteSpace: 'pre-wrap' }}>
                      {selectedSession.notes}
                    </Typography>
                  </CardContent>
                </Card>
              )}

              {/* Code Editor Data */}
              {(selectedSession.status === 'active' || selectedSession.status === 'completed') && (
                <Card>
                  <CardContent>
                    <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', fontWeight: 600 }}>
                      Code Editor Data
                    </Typography>
                    {loadingCodeData ? (
                      <Box sx={{ display: 'flex', alignItems: 'center', mt: 2 }}>
                        <CircularProgress size={20} sx={{ mr: 1 }} />
                        <Typography variant="body2">Loading code data...</Typography>
                      </Box>
                    ) : codeEditorData ? (
                      <Box sx={{ mt: 2 }}>
                        <Box sx={{ mb: 2 }}>
                          <Typography variant="body2" color="text.secondary">Language</Typography>
                          <Chip 
                            label={codeEditorData.language} 
                            size="small" 
                            sx={{ mt: 0.5, fontFamily: 'monospace' }}
                          />
                        </Box>
                        <Box sx={{ mb: 2 }}>
                          <Typography variant="body2" color="text.secondary">Last Updated</Typography>
                          <Typography variant="body1">
                            {new Date(codeEditorData.last_updated_at).toLocaleString()}
                          </Typography>
                        </Box>
                        <Box sx={{ mb: 2 }}>
                          <Typography variant="body2" color="text.secondary">Code Preview</Typography>
                          <Paper 
                            sx={{ 
                              mt: 1, 
                              p: 2, 
                              bgcolor: '#f5f5f5', 
                              fontFamily: 'monospace', 
                              fontSize: '0.875rem',
                              maxHeight: 200,
                              overflow: 'auto'
                            }}
                          >
                            <pre style={{ margin: 0, whiteSpace: 'pre-wrap' }}>
                              {codeEditorData.code_content.substring(0, 500)}
                              {codeEditorData.code_content.length > 500 && '...'}
                            </pre>
                          </Paper>
                        </Box>
                        <Button
                          variant="outlined"
                          startIcon={<CodeIcon />}
                          onClick={() => navigate(`/editor?room=${selectedSession.room_code}`)}
                          size="small"
                        >
                          View Full Code
                        </Button>
                      </Box>
                    ) : (
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                        No code editor data available for this session
                      </Typography>
                    )}
                  </CardContent>
                </Card>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setViewDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

